import React, { FC } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  EmployeeRouteMetaDocument,
  EmployeeSelectorAllDocument,
  EmployeesPersonalDataDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'
import { employeeUser } from 'modules/core/store/collections/users'

import AddressForm from 'modules/employees/lib/forms/Personal/Address'
import EmployeeContactForm from 'modules/employees/lib/forms/Personal/EmployeeContactForm'
import ExtraInformationForm from 'modules/employees/lib/forms/Personal/ExtraInformation'
import GeneralForm from 'modules/employees/lib/forms/Personal/General'
import IdentificationForm from 'modules/employees/lib/forms/Personal/Identification'
import IrpfForm from 'modules/employees/lib/forms/Personal/Irpf'
import PersonalSituationForm from 'modules/employees/lib/forms/Personal/PersonalSituation'
import TaxResidenceForm from 'modules/employees/lib/forms/Personal/TaxResidence'
import UserForm from 'modules/employees/lib/forms/Personal/User'
import Employee, { buildEmployee } from 'modules/employees/store/models/Employee'
import taxResidences from 'modules/payroll_bundle/store/collections/taxResidences'

export enum FormTypes {
  USER = 'USER',
  GENERAL = 'GENERAL',
  IDENTIFICATION = 'IDENTIFICATION',
  CONTACT = 'CONTACT',
  ADDRESS = 'ADDRESS',
  TAX_RESIDENCE = 'TAX_RESIDENCE',
  IRPF = 'IRPF',
  EXTRA_INFORMATION = 'EXTRA_INFORMATION',
  PERSONAL_SITUATION = 'PERSONAL_SITUATION',
}

type FormTypeKyes = keyof typeof FormTypes

export enum EmployeePersonalActionTypes {
  SET_DEFAULT_DATA = 'SET_DEFAULT_DATA',
}

interface State {
  employee: Employee
  userForm: UserForm
  generalForm: GeneralForm
  identificationForm: IdentificationForm
  contactForm: EmployeeContactForm
  addressForm: AddressForm
  taxResidenceForm: TaxResidenceForm
  irpfForm: IrpfForm
  extraInformationForm: ExtraInformationForm
  personalSituationForm: PersonalSituationForm
}

interface EmployeePersonalContextProps extends State {
  saving: boolean
  isDirty: boolean
  isSpanish: boolean
  isResident: boolean
  isContractCategories: boolean
  disableSectionSaveButtons: boolean
  onSubmit: (keys?: FormTypeKyes[]) => void
}

const initialState = () => ({
  userForm: new UserForm({}),
  employee: buildEmployee(),
  generalForm: new GeneralForm({}),
  identificationForm: new IdentificationForm({}, undefined),
  addressForm: new AddressForm({}),
  contactForm: new EmployeeContactForm({}),
  taxResidenceForm: new TaxResidenceForm({}),
  irpfForm: new IrpfForm({}),
  extraInformationForm: new ExtraInformationForm({}),
  personalSituationForm: new PersonalSituationForm({}),
})

const EmployeePersonalProviderContext = React.createContext<EmployeePersonalContextProps | null>(
  null
)

interface EmployeePersonalProviderProps {
  children: React.ReactNode
  employee: Employee
  isContractCategories?: boolean
  disableSectionSaveButtons?: boolean
}

interface Payload {
  employee: Employee
  userForm: UserForm
  generalForm: GeneralForm
  identificationForm: IdentificationForm
  addressForm: AddressForm
  contactForm: EmployeeContactForm
  taxResidenceForm: TaxResidenceForm
  irpfForm: IrpfForm
  extraInformationForm: ExtraInformationForm
  personalSituationForm: PersonalSituationForm
}

interface Action {
  type: EmployeePersonalActionTypes
  payload: Payload
}

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case EmployeePersonalActionTypes.SET_DEFAULT_DATA:
      return {
        ...state,
        ...action.payload,
      }

    default:
      throw new Error(`No such type: ${action.type}`)
  }
}

export const EmployeePersonalProvider: FC<EmployeePersonalProviderProps> = observer(
  ({ employee, disableSectionSaveButtons, isContractCategories, children }) => {
    const [state, dispatch] = React.useReducer(reducer, initialState())
    const isSpanish = employee.attributes.nationality === 'es'
    const [saving, setSaving] = React.useState(false)
    const apolloClient = useApolloClient()
    const employeeTaxResidence = taxResidences.find({
      employee_id: employee.attributes.id,
    })
    const isResident =
      !employeeTaxResidence && isSpanish ? true : employeeTaxResidence?.attributes.is_resident

    const {
      userForm,
      generalForm,
      identificationForm,
      contactForm,
      addressForm,
      taxResidenceForm,
      irpfForm,
      extraInformationForm,
      personalSituationForm,
    } = state

    const fetchResidences = async () =>
      await taxResidences.fetch({
        data: {
          employee_id: employee.attributes.id,
        },
      })

    useEffect(() => {
      const {
        birthday_on,
        gender,
        nationality,
        phone_number,
        payment_frequency,
        preferred_name,
        pronouns,
        irpf,
        country_metadata,
        share_birthday,
        address_line_1,
        address_line_2,
        city,
        country,
        postal_code,
        state: employeeState,
        contact_name,
        contact_number,
      } = employee.attributes
      fetchResidences()
      dispatch({
        type: EmployeePersonalActionTypes.SET_DEFAULT_DATA,
        payload: {
          ...initialState(),
          employee,
          userForm: new UserForm(employeeUser(employee).toJS()),
          generalForm: new GeneralForm({
            birthday_on: birthday_on ?? '',
            gender: gender ?? '',
            nationality: nationality ?? '',
            phone_number: phone_number ?? '',
            payment_frequency: payment_frequency ?? '',
            preferred_name: preferred_name ?? '',
            pronouns: pronouns ?? '',
            irpf: irpf ?? 0,
            country_metadata: country_metadata ?? {},
            share_birthday: share_birthday ?? false,
          }),
          identificationForm: new IdentificationForm(
            {
              identifier: employee.attributes.identifier ?? '',
              identifier_type: employee.attributes.identifier_type ?? '',
              identifier_expiration_date: employee.attributes.identifier_expiration_date ?? '',
              country_metadata: country_metadata ?? {},
            },
            employee.attributes.fiscal_country
          ),
          addressForm: new AddressForm({
            address_line_1: address_line_1 ?? '',
            address_line_2: address_line_2 ?? '',
            city: city ?? '',
            country: country ?? '',
            postal_code: postal_code ?? '',
            state: employeeState ?? '',
          }),
          contactForm: new EmployeeContactForm({
            contact_name: contact_name ?? '',
            contact_number: contact_number ?? '',
          }),
          extraInformationForm: new ExtraInformationForm({
            ...(employeeTaxResidence ? employeeTaxResidence.toJS() : {}),
            employee_id: employee.attributes.id,
          }),
          taxResidenceForm: new TaxResidenceForm({
            ...(employeeTaxResidence ? employeeTaxResidence.toJS() : {}),
            employee_id: employee.attributes.id,
            is_resident: isResident,
          }),
          irpfForm: new IrpfForm({
            country_metadata: country_metadata,
          }),
          personalSituationForm: new PersonalSituationForm({
            disability_percentage_cents: employee.attributes.disability_percentage_cents,
          }),
        },
      })
    }, [employee.id, employeeTaxResidence]) // eslint-disable-line react-hooks/exhaustive-deps

    const formsByType = {
      [FormTypes.USER]: userForm,
      [FormTypes.GENERAL]: generalForm,
      [FormTypes.IDENTIFICATION]: identificationForm,
      [FormTypes.CONTACT]: contactForm,
      [FormTypes.ADDRESS]: addressForm,
      [FormTypes.TAX_RESIDENCE]: taxResidenceForm,
      [FormTypes.IRPF]: irpfForm,
      [FormTypes.EXTRA_INFORMATION]: extraInformationForm,
      [FormTypes.PERSONAL_SITUATION]: personalSituationForm,
    }

    const onSubmit = (keys?: FormTypeKyes[]) => {
      setSaving(true)
      // when disableSectionSaveButtons is true, we want to save all the forms
      const forms = keys
        ? keys.map((key) => formsByType[key])
        : disableSectionSaveButtons
        ? Object.values(formsByType)
        : []

      const dirtyForms = forms.filter((form) => form.isDirty)

      asyncAction(async () => {
        for (const form of dirtyForms) {
          if (form === userForm) await form.save(employeeUser(employee))
          else if ([taxResidenceForm, extraInformationForm].includes(form))
            employeeTaxResidence
              ? await form.save(employeeTaxResidence)
              : await form.create(taxResidences)
          else if (form === identificationForm) {
            if (!form.values.identifier) {
              form.setValues({ identifier_type: null })
              form.setValues({ identifier_expiration_date: null })
            }
            await form.save(employee)
          } else await form.save(employee)
        }
      })
        .then(() =>
          apolloClient.refetchQueries({
            include: [
              EmployeeRouteMetaDocument,
              EmployeeSelectorAllDocument,
              EmployeesPersonalDataDocument,
            ],
          })
        )
        .finally(() => setSaving(false))
    }

    const isDirty =
      contactForm.isDirty ||
      userForm.isDirty ||
      generalForm.isDirty ||
      identificationForm.isDirty ||
      addressForm.isDirty ||
      taxResidenceForm.isDirty ||
      irpfForm.isDirty ||
      (!employeeTaxResidence && isSpanish) ||
      extraInformationForm.isDirty ||
      personalSituationForm.isDirty

    return (
      <EmployeePersonalProviderContext.Provider
        value={{
          ...state,
          isDirty,
          onSubmit,
          isResident,
          isSpanish,
          saving,
          isContractCategories: !!isContractCategories,
          disableSectionSaveButtons: !!disableSectionSaveButtons,
        }}
      >
        {children}
      </EmployeePersonalProviderContext.Provider>
    )
  }
)

export const useEmployeePersonalProvider = (): EmployeePersonalContextProps => {
  const EmployeePersonalModule = React.useContext(EmployeePersonalProviderContext)

  if (!EmployeePersonalModule)
    throw new Error('EmployeePersonalProvider must be used within an EmployeePersonalModule')

  return EmployeePersonalModule
}
